import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import React, { Component, ReactNode } from "react";

import { delay } from "@formant/core/common/delay";
import { fork } from "@formant/core/common/fork";
import { IChallenge } from "@formant/core/model/IChallenge";
import { animate } from "@formant/ui/utils/animate";

import { adminApi } from "../config";
import { authStore } from "../stores";
import { Auth } from "./Auth/Auth";
import * as styles from "./CheckAuthentication.scss";
import { Login } from "./Login/Login";
import { ResetPassword } from "./ResetPassword";

interface ICheckAuthenticationProps {
    children?: React.ReactNode;
}

@observer
export class CheckAuthentication extends Component<ICheckAuthenticationProps> {
    @observable
    private transitioning: boolean = false;
    private challenge?: IChallenge;
    private loggedIn?: boolean;

    componentDidMount(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const shouldLogout = urlParams.get("logout") === "true";
        if (shouldLogout) {
            authStore.logout();
            // remove logout param from url
            const url = new URL(window.location.href);
            url.searchParams.delete("logout");
            window.history.replaceState({}, "", url.toString());
        }
    }

    public render() {
        fork(this.update());

        return (
            <div
                className={classnames(
                    styles["check-authentication"],
                    this.transitioning && styles.transitioning,
                )}
            >
                {this.child}
            </div>
        );
    }

    private get child(): ReactNode {
        const { challenge, loggedIn } = this;
        const urlParams = new URLSearchParams(window.location.search);
        const isNewAuthFlow = urlParams.get("newAuth") === "true";

        if (loggedIn === undefined) {
            return false;
        }

        if (challenge && challenge.type === "new-password-required") {
            return (
                <ResetPassword
                    userId={challenge.userId}
                    email={challenge.email}
                    session={challenge.session}
                />
            );
        }

        if (!loggedIn) {
            if (isNewAuthFlow) {
                return <Auth />;
            }
            return <Login />;
        }

        return this.props.children;
    }

    private async update() {
        const { challenge, userId, refreshToken, currentToken } = authStore;
        const loggedIn = userId !== undefined;

        const tokenForRedirect =
            refreshToken ??
            (authStore as { refreshTokenFromStorage?: string })
                .refreshTokenFromStorage ??
            currentToken;

        // Handle redirect if user is logged in and redirectUrl exists
        if (loggedIn && tokenForRedirect) {
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUrl = urlParams.get("redirectUrl");
            const includeToken = urlParams.get("includeToken") === "true";
            const shouldLogout = urlParams.get("logout") === "true";

            if (shouldLogout) {
                return;
            }

            authStore.setTokenToCookieStorage(tokenForRedirect);

            if (redirectUrl) {
                // Create URL object to properly handle query parameters
                try {
                    const url = new URL(decodeURIComponent(redirectUrl));

                    // Treat as OAuth flow only when the redirect URL has a state param; then require
                    // the URL to be the admin-api OAuth callback. No state = platform-app / auth-lib flow.
                    if (includeToken) {
                        const state = url.searchParams.get("state");
                        if (state) {
                            const adminApiOrigin = new URL(adminApi).origin;
                            const isAdminApi = url.origin === adminApiOrigin;
                            const isOauthCallback =
                                url.pathname.endsWith(
                                    "/oauth/authorize/callback",
                                ) ||
                                url.pathname.endsWith(
                                    "/oauth/authorize/callback/",
                                );
                            if (!isAdminApi || !isOauthCallback) {
                                const current = new URL(window.location.href);
                                current.searchParams.delete("redirectUrl");
                                current.searchParams.delete("includeToken");
                                current.searchParams.set(
                                    "loginError",
                                    "invalidOauthRedirect",
                                );
                                window.history.replaceState(
                                    {},
                                    "",
                                    current.toString(),
                                );
                                return;
                            }
                        }
                    }
                    if (includeToken) {
                        url.searchParams.set("token", tokenForRedirect);
                    }
                    window.location.href = url.toString();
                    return; // Stop further execution since we're redirecting
                } catch (e) {
                    // Invalid URL format. Strip redirect params and show a user-facing error.
                    const url = new URL(window.location.href);
                    url.searchParams.delete("redirectUrl");
                    url.searchParams.delete("includeToken");
                    url.searchParams.set("loginError", "invalidRedirectUrl");
                    window.history.replaceState({}, "", url.toString());
                }
            }
        }

        if (challenge === this.challenge && loggedIn === this.loggedIn) {
            return;
        }

        await delay();
        this.transitioning = true;
        await animate();

        this.challenge = challenge;
        this.loggedIn = loggedIn;
        this.transitioning = false;
    }
}
